// src/hooks/useVersionCheck.ts
import { useState, useEffect, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import {
	initVersionCheck,
	forceCheckForUpdates,
	getAppVersion,
	VersionCheckResult,
} from '../utils/versionManager';

interface UseVersionCheckReturn {
	/** Current app version */
	version: string;
	/** Whether the initial check has completed */
	isChecking: boolean;
	/** Whether a manual update check is in progress */
	isUpdating: boolean;
	/** Manually trigger an update check */
	checkForUpdates: () => Promise<void>;
	/** The result of the last version check */
	checkResult: VersionCheckResult | null;
}

/**
 * Hook to handle app version checking and updates
 *
 * Features:
 * - Automatically checks version on mount
 * - Shows notification when app was just updated
 * - Provides manual update check function
 * - Handles all the complexity of cache clearing/reload
 *
 * Usage:
 * ```tsx
 * function App() {
 *   const { version, checkForUpdates, isUpdating } = useVersionCheck();
 *
 *   return (
 *     <button onClick={checkForUpdates} disabled={isUpdating}>
 *       Check for Updates
 *     </button>
 *   );
 * }
 * ```
 */
export function useVersionCheck(): UseVersionCheckReturn {
	const [isChecking, setIsChecking] = useState(true);
	const [isUpdating, setIsUpdating] = useState(false);
	const [checkResult, setCheckResult] = useState<VersionCheckResult | null>(null);

	// Run version check on mount
	useEffect(() => {
		let mounted = true;

		async function runCheck() {
			try {
				const result = await initVersionCheck();

				if (!mounted) return;

				setCheckResult(result);
				setIsChecking(false);

				// Show notification if we just updated
				if (result.justUpdated) {
					notifications.show({
						title: 'App Updated',
						message: `Bill Tracker has been updated to version ${result.currentVersion}`,
						color: 'green',
						autoClose: 5000,
					});
				}
			} catch (error) {
				console.error('Version check failed:', error);
				if (mounted) {
					setIsChecking(false);
				}
			}
		}

		runCheck();

		return () => {
			mounted = false;
		};
	}, []);

	// Manual update check function
	const checkForUpdates = useCallback(async () => {
		if (isUpdating) return;

		setIsUpdating(true);

		try {
			// Show loading notification
			notifications.show({
				id: 'update-check',
				title: 'Checking for Updates',
				message: 'Please wait...',
				loading: true,
				autoClose: false,
				withCloseButton: false,
			});

			// This will trigger a reload if caches are cleared
			await forceCheckForUpdates();

			// If we get here, the page didn't reload (shouldn't happen normally)
			notifications.update({
				id: 'update-check',
				title: 'Up to Date',
				message: 'You have the latest version.',
				color: 'green',
				loading: false,
				autoClose: 3000,
				withCloseButton: true,
			});
		} catch (error) {
			console.error('Manual update check failed:', error);
			notifications.update({
				id: 'update-check',
				title: 'Update Failed',
				message: 'Could not check for updates. Please try again.',
				color: 'red',
				loading: false,
				autoClose: 3000,
				withCloseButton: true,
			});
		} finally {
			setIsUpdating(false);
		}
	}, [isUpdating]);

	return {
		version: getAppVersion(),
		isChecking,
		isUpdating,
		checkForUpdates,
		checkResult,
	};
}

export default useVersionCheck;
